import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2, Trash2, AlertTriangle } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/lib/supabase'

interface DeletePatientModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  patient: {
    id: string
    full_name: string
  } | null
  onSuccess?: () => void
}

export function DeletePatientModal({
  open,
  onOpenChange,
  patient,
  onSuccess,
}: DeletePatientModalProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [confirmName, setConfirmName] = useState('')
  const { toast } = useToast()

  const isConfirmValid = confirmName.toLowerCase() === patient?.full_name.toLowerCase()

  const handleDelete = async () => {
    if (!patient || !isConfirmValid) return

    setIsLoading(true)

    try {
      const { error } = await supabase
        .from('patients')
        .update({
          status: 'deleted',
          updated_at: new Date().toISOString(),
        })
        .eq('id', patient.id)

      if (error) throw error

      toast({
        title: 'Paciente excluido',
        description: `${patient.full_name} foi removido com sucesso.`,
      })

      setConfirmName('')
      onOpenChange(false)
      onSuccess?.()
    } catch (error) {
      console.error('Error deleting patient:', error)
      toast({
        title: 'Erro',
        description: 'Nao foi possivel excluir o paciente.',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setConfirmName('')
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="h-5 w-5" />
            Excluir Paciente
          </DialogTitle>
          <DialogDescription>
            Confirme a exclusao do paciente digitando o nome completo abaixo.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-red-800">
                  Tem certeza que deseja excluir este paciente?
                </p>
                <p className="text-sm text-red-700">
                  O paciente <strong>{patient?.full_name}</strong> sera removido da listagem
                  e nao aparecera mais no sistema.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-name">
              Digite <strong>{patient?.full_name}</strong> para confirmar
            </Label>
            <Input
              id="confirm-name"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder="Nome do paciente"
              autoComplete="off"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isLoading || !isConfirmValid}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Excluir Paciente
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
